import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../AdminManageCategories/AdminManageCategories.css';
import { buildCategoryTree } from '../../firebase/categories';
import { useCategoryManagement } from '../../hooks/useCategoryManagement';

function ProductTabBar({ active }) {
    const navigate = useNavigate();
    const tabs = [
        { label: 'Manage Products', path: '/admin/products/manage' },
        { label: 'Manage Category', path: '/admin/products/categories' },
        { label: 'Manage Subcategory', path: '/admin/products/subcategories' },
        { label: 'Manage Submenus', path: '/admin/products/submenus' },
        { label: 'Manage Attributes', path: '/admin/products/attributes' },
        { label: 'Manage Inventory', path: '/admin/products/inventory' },
    ];
    return <div className="mc-tab-bar">{tabs.map((tab) => <button key={tab.path} className={`mc-tab${active === tab.label ? ' mc-tab--active' : ''}`} onClick={() => navigate(tab.path)}>{tab.label}</button>)}</div>;
}

function StatusToggle({ status, onChange }) {
    const checked = status === 'active';
    return (
        <label className="mc-toggle">
            <input type="checkbox" checked={checked} onChange={onChange} />
            <span className="mc-toggle-track"><span className="mc-toggle-thumb" /></span>
        </label>
    );
}

function ManageSubmenusModal({ subcategory, onClose, onSave, isClearMode, saving }) {
    const [name, setName] = useState('');
    const [submenus, setSubmenus] = useState(subcategory.submenus || []);

    const add = () => {
        const value = name.trim();
        if (!value || submenus.some((item) => item.name.toLowerCase() === value.toLowerCase())) return;
        setSubmenus((prev) => [...prev, { name: value, status: 'active' }]);
        setName('');
    };

    const toggleStatus = (index) => {
        setSubmenus((prev) => prev.map((item, i) => i === index ? { ...item, status: item.status === 'active' ? 'inactive' : 'active' } : item));
    };

    return (
        <div className="mc-modal-overlay" onClick={onClose}>
            <div className="mc-dialog mc-dialog--wide" onClick={(event) => event.stopPropagation()}>
                <div className="mc-modal-header">
                    <h2 className="mc-modal-title">
                        {isClearMode ? `Clear Submenus: ${subcategory.name}` : `Manage Submenus: ${subcategory.name}`}
                    </h2>
                    <button className="mc-modal-close" onClick={onClose}>×</button>
                </div>
                {!isClearMode && (
                    <div className="mc-field">
                        <label>Add Submenu</label>
                        <div className="mc-sub-input-row">
                            <input value={name} onChange={(event) => setName(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && add()} placeholder="Add submenu" />
                            <button type="button" className="mc-sub-add-btn" onClick={add}>Add</button>
                        </div>
                    </div>
                )}
                <div className="mc-structured-list">
                    {submenus.length === 0 ? <p className="mc-sub-empty">{isClearMode ? 'No submenus to clear.' : 'No submenus yet.'}</p> : submenus.map((submenu, index) => (
                        <div key={`${submenu.id || submenu.name}-${index}`} className="mc-tree-item mc-tree-item--submenu">
                            <span>{submenu.name}</span>
                            {!isClearMode && <StatusToggle status={submenu.status || 'active'} onChange={() => toggleStatus(index)} />}
                            {!isClearMode && <button type="button" className="mc-mini-danger" onClick={() => setSubmenus((prev) => prev.filter((_, i) => i !== index))}>Delete</button>}
                        </div>
                    ))}
                </div>
                <div className="mc-modal-footer">
                    <button className="mc-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="mc-btn-primary" onClick={() => onSave(submenus)} disabled={saving}>
                        {saving ? 'Saving...' : (isClearMode ? 'Confirm Clear' : 'Save Changes')}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function AdminManageSubmenus() {
    const { categories, loading, error, fetchCategories, replaceSubmenuEntries } = useCategoryManagement([]);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [target, setTarget] = useState(null);
    const [clearTarget, setClearTarget] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => { fetchCategories(); }, [fetchCategories]);
    const rows = useMemo(() => buildCategoryTree(categories).flatMap((main) => main.subcategories.map((subcategory) => ({ ...subcategory, parentName: main.name }))), [categories]);

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        let result = rows;
        if (statusFilter !== 'all') {
            result = result.filter((row) => row.status === statusFilter);
        }
        if (!q) return result;
        return result.filter((row) =>
            (row.name || '').toLowerCase().includes(q) ||
            (row.parentName || '').toLowerCase().includes(q)
        );
    }, [search, rows, statusFilter]);

    useEffect(() => { setPage(1); }, [search, statusFilter]);
    const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
    const paginatedRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);

    const save = async (submenus) => {
        setSaving(true);
        setActionError(null);
        const result = await replaceSubmenuEntries(target, submenus);
        setSaving(false);
        if (result.error) {
            setActionError(result.error);
            return;
        }
        // Ensure fresh data is fetched before closing modal
        await fetchCategories();
        setTarget(null);
    };

    const clearSubmenus = async () => {
        setSaving(true);
        setActionError(null);
        const result = await replaceSubmenuEntries(clearTarget, []);
        setSaving(false);
        if (result.error) {
            setActionError(result.error);
            return;
        }
        await fetchCategories();
        setClearTarget(null);
    };

    const hasActiveFilters = statusFilter !== 'all' || search.trim() !== '';

    return (
        <div className="mc-page">
            <ProductTabBar active="Manage Submenus" />
            {loading && !saving && <div className="mc-info-banner">Loading submenus...</div>}
            {(error || actionError) && <div className="mc-info-banner">{error || actionError}</div>}

            <div className="mc-toolbar">
                <div className="mc-toolbar-left">
                    <div className="mc-entries-label">
                        Show
                        <select className="mc-entries-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                            <option value="all">All Statuses</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                    {hasActiveFilters && (
                        <button className="mc-filter-clear" onClick={() => { setStatusFilter('all'); setSearch(''); setPage(1); }}>Clear filters</button>
                    )}
                </div>
                <div className="mc-toolbar-right">
                    <div className="mc-search-wrap">
                        <input className="mc-search" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="Search subcategories..." />
                    </div>
                </div>
            </div>

            <div className="mc-table-wrap">
                <table className="mc-table">
                    <thead><tr><th>Main Category</th><th>Subcategory</th><th>Number of Submenus</th><th>Actions</th></tr></thead>
                    <tbody>
                        {paginatedRows.length === 0 ? <tr><td colSpan={4} className="mc-empty">No subcategories available.</td></tr> : paginatedRows.map((row) => (
                            <tr key={row.id}><td>{row.parentName}</td><td className="mc-cat-name">{row.name}</td><td>{row.submenus.length}</td><td><div className="mc-actions"><button className="mc-action-btn mc-action-btn--edit" onClick={() => setTarget(row)} disabled={row.isLegacy} title="Manage submenus for this subcategory"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg><span className="mc-action-label">Edit</span></button><button className="mc-action-btn mc-action-btn--delete" onClick={() => setClearTarget(row)} title="Clear all submenus"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg><span className="mc-action-label">Clear</span></button></div></td></tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {filteredRows.length > pageSize && (
                <div className="mc-pagination">
                    <select className="mc-entries-select" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                        {[10, 15, 20, 25].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span className="mc-result-count">
                        Showing {Math.min((page - 1) * pageSize + 1, filteredRows.length)}–{Math.min(page * pageSize, filteredRows.length)} of {filteredRows.length}
                    </span>
                    <button className="mc-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Previous</button>
                    <span className="mc-page-divider">|</span>
                    <button className="mc-page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
                </div>
            )}

            {target && <ManageSubmenusModal subcategory={target} onClose={() => setTarget(null)} onSave={save} isClearMode={false} saving={saving} />}
            {clearTarget && <ManageSubmenusModal subcategory={clearTarget} onClose={() => setClearTarget(null)} onSave={clearSubmenus} isClearMode={true} saving={saving} />}
        </div>
    );
}